/**
 * PREPARAZIONE AUTOMATICA DELLA FOTO (Týpos, 13-14 ott 2026). Davide: «la foto va ridotta e preparata come uno stencil in
 * automatico: il cliente deve solo caricarla»; poi (14 ott, con disegni di bambini e pagine di quaderno): «togliere i
 * quadretti o le righe dei fogli e ripassare al meglio i disegni colorati in bianco e nero»; dettaglio minimo del laser
 * 0,1 mm.
 *
 * Nel browser, un attimo dopo la scelta del file e PRIMA che parta verso ShapeDiver:
 *  1. legge la foto girandola come la mostra il telefono (EXIF) e la riduce a LATO_MAX pixel sul lato lungo;
 *  2. stima il COLORE DELLA CARTA punto per punto (per canale: foto rimpicciolita, "massimo" locale che cancella i tratti,
 *     sfocatura; mai sotto FONDO_MIN volte la carta piu' chiara, cosi' le grandi zone scure restano piene): corregge le
 *     ombre e i fogli colorati (es. giallini);
 *  3. INCHIOSTRO = quanto il punto si allontana dalla carta nel canale PEGGIORE (rapporto minimo fra R, G, B): cosi' anche
 *     un tratto giallo o azzurro chiaro su carta bianca diventa nero, non solo quelli scuri;
 *  4. soglia automatica (Otsu, tenuta fra SOGLIA_MIN e SOGLIA_MAX) -> bianco o nero;
 *  5. RIGHE E QUADRETTI: cerca le linee dritte che attraversano il foglio (proiezioni su angoli da -4° a +4°: una riga del
 *     quaderno e' un picco stretto e lungo, una riga di testo no) e le toglie, ma solo nei punti in cui il segno e' sottile
 *     come la riga: le lettere e i tratti che la attraversano restano;
 *  6. toglie le macchioline e chiude i buchini, ritaglia attorno al disegno;
 *  7. SPESSORE MINIMO 0,1 mm: con il disegno grande LATO_INCISO_MM sul gioiello, i tratti piu' sottili di 0,1 mm vengono
 *     ingrossati fino a 0,1 mm (il laser non incide bene sotto); poi di nuovo via le macchioline sotto quella misura;
 *  8. consegna un PNG in bianco e nero.
 * Il modello (Rooster) riceve cosi' un'immagine gia' pulita: qualunque soglia del modello va bene.
 */

const LATO_MAX = 1500;
const FONDO_MIN = 0.55;
const SOGLIA_MIN = 0.45;
const SOGLIA_MAX = 0.85;
const SOGLIA_DEBOLE = 0.93; // per trovare anche le righe chiare del quaderno
const MARGINE = 0.04;
const DETTAGLIO_MIN_MM = 0.1; // Davide, 14 ott 2026: dettaglio minimo del laser
const LATO_INCISO_MM = 14; // lato lungo tipico dell'incisione sul gioiello (cuore 15, quadrato 17, anello 13,7, goccia 11)

export type InfoStencil = {
	larghezza: number;
	altezza: number;
	soglia: number;
	inchiostro: number;
	righe: number; // righe/quadretti tolti
	svuotati: number; // punti dei colori pieni ridotti a contorno
	bordi: number; // zone piene sul bordo della foto tolte
	spessore_px: number; // spessore medio dei tratti (prima di ingrossare)
	ingrossato_px: number; // raggio di ingrossamento
	ms: number;
};

function tela(l: number, a: number): HTMLCanvasElement {
	const c = document.createElement("canvas");
	c.width = l;
	c.height = a;
	return c;
}

function massimoLocale(v: Float32Array, l: number, a: number, r: number): Float32Array {
	const tmp = new Float32Array(v.length);
	const out = new Float32Array(v.length);
	for (let y = 0; y < a; y++)
		for (let x = 0; x < l; x++) {
			let m = 0;
			for (let k = Math.max(0, x - r); k <= Math.min(l - 1, x + r); k++) m = Math.max(m, v[y * l + k]);
			tmp[y * l + x] = m;
		}
	for (let y = 0; y < a; y++)
		for (let x = 0; x < l; x++) {
			let m = 0;
			for (let k = Math.max(0, y - r); k <= Math.min(a - 1, y + r); k++) m = Math.max(m, tmp[k * l + x]);
			out[y * l + x] = m;
		}
	return out;
}

function sfoca(v: Float32Array, l: number, a: number, r: number): Float32Array {
	if (r < 1) return v;
	const tmp = new Float32Array(v.length);
	const out = new Float32Array(v.length);
	for (let y = 0; y < a; y++) {
		let s = 0, n = 0;
		for (let k = 0; k <= Math.min(l - 1, r); k++) { s += v[y * l + k]; n++; }
		for (let x = 0; x < l; x++) {
			tmp[y * l + x] = s / n;
			const fuori = x - r, dentro = x + r + 1;
			if (fuori >= 0) { s -= v[y * l + fuori]; n--; }
			if (dentro < l) { s += v[y * l + dentro]; n++; }
		}
	}
	for (let x = 0; x < l; x++) {
		let s = 0, n = 0;
		for (let k = 0; k <= Math.min(a - 1, r); k++) { s += tmp[k * l + x]; n++; }
		for (let y = 0; y < a; y++) {
			out[y * l + x] = s / n;
			const fuori = y - r, dentro = y + r + 1;
			if (fuori >= 0) { s -= tmp[fuori * l + x]; n--; }
			if (dentro < a) { s += tmp[dentro * l + x]; n++; }
		}
	}
	return out;
}

/** fondo (colore della carta) di un canale, alla risoluzione piena */
function fondoCanale(c: Float32Array, l: number, a: number): Float32Array {
	const f = 8, ls = Math.max(1, Math.ceil(l / f)), as = Math.max(1, Math.ceil(a / f));
	let p: Float32Array = new Float32Array(ls * as);
	for (let y = 0; y < as; y++)
		for (let x = 0; x < ls; x++) {
			let s = 0, n = 0;
			for (let yy = y * f; yy < Math.min(a, y * f + f); yy++) for (let xx = x * f; xx < Math.min(l, x * f + f); xx++) { s += c[yy * l + xx]; n++; }
			p[y * ls + x] = s / n;
		}
	p = sfoca(massimoLocale(p, ls, as, 4), ls, as, 3);
	const ord = Float32Array.from(c).sort();
	const minimo = (ord[Math.floor(ord.length * 0.9)] || 1) * FONDO_MIN;
	const out = new Float32Array(l * a);
	for (let y = 0; y < a; y++) {
		const fy = Math.min(as - 1.001, Math.max(0, (y + 0.5) / f - 0.5));
		const y0 = Math.floor(fy), ty = fy - y0, y1 = Math.min(as - 1, y0 + 1);
		for (let x = 0; x < l; x++) {
			const fx = Math.min(ls - 1.001, Math.max(0, (x + 0.5) / f - 0.5));
			const x0 = Math.floor(fx), tx = fx - x0, x1 = Math.min(ls - 1, x0 + 1);
			const v = (p[y0 * ls + x0] * (1 - tx) + p[y0 * ls + x1] * tx) * (1 - ty) + (p[y1 * ls + x0] * (1 - tx) + p[y1 * ls + x1] * tx) * ty;
			out[y * l + x] = Math.max(minimo, v, 0.02);
		}
	}
	return out;
}

function otsu(v: Float32Array): number {
	const N = 240, h = new Float64Array(N);
	for (let i = 0; i < v.length; i++) h[Math.min(N - 1, Math.max(0, Math.floor((v[i] / 1.2) * N)))]++;
	let tot = 0, somma = 0;
	for (let i = 0; i < N; i++) { tot += h[i]; somma += i * h[i]; }
	let sB = 0, wB = 0, migliore = 0, soglia = N / 2;
	for (let i = 0; i < N; i++) {
		wB += h[i];
		if (!wB) continue;
		const wF = tot - wB;
		if (!wF) break;
		sB += i * h[i];
		const mB = sB / wB, mF = (somma - sB) / wF;
		const var_ = wB * wF * (mB - mF) * (mB - mF);
		if (var_ > migliore) { migliore = var_; soglia = i; }
	}
	return ((soglia + 0.5) / N) * 1.2;
}

/** toglie le macchie (componenti a 8 vicini) del colore "valore" piu' piccole di minArea */
function togliMacchie(b: Uint8Array, l: number, a: number, valore: number, minArea: number): number {
	const visto = new Uint8Array(b.length);
	const pila = new Int32Array(b.length);
	const comp: number[] = [];
	let tolte = 0;
	for (let i = 0; i < b.length; i++) {
		if (visto[i] || b[i] !== valore) continue;
		let p = 0;
		pila[p++] = i;
		visto[i] = 1;
		comp.length = 0;
		let tocca = false;
		while (p) {
			const j = pila[--p];
			comp.push(j);
			const x = j % l, y = (j - x) / l;
			if (x === 0 || y === 0 || x === l - 1 || y === a - 1) tocca = true;
			for (let dy = -1; dy <= 1; dy++)
				for (let dx = -1; dx <= 1; dx++) {
					const xx = x + dx, yy = y + dy;
					if (xx < 0 || yy < 0 || xx >= l || yy >= a) continue;
					const k = yy * l + xx;
					if (!visto[k] && b[k] === valore) { visto[k] = 1; pila[p++] = k; }
				}
		}
		if (comp.length < minArea && !(valore === 0 && tocca)) {
			for (const j of comp) b[j] = 1 - valore;
			tolte++;
		}
	}
	return tolte;
}

/** lunghezza della striscia di nero che passa per ogni punto, in verticale (vert=true) o in orizzontale */
function strisce(b: Uint8Array, l: number, a: number, vert: boolean): Uint16Array {
	const out = new Uint16Array(b.length);
	if (vert) {
		for (let x = 0; x < l; x++) {
			let y = 0;
			while (y < a) {
				if (!b[y * l + x]) { y++; continue; }
				let y2 = y;
				while (y2 < a && b[y2 * l + x]) y2++;
				for (let k = y; k < y2; k++) out[k * l + x] = Math.min(65535, y2 - y);
				y = y2;
			}
		}
	} else {
		for (let y = 0; y < a; y++) {
			let x = 0;
			while (x < l) {
				if (!b[y * l + x]) { x++; continue; }
				let x2 = x;
				while (x2 < l && b[y * l + x2]) x2++;
				for (let k = x; k < x2; k++) out[y * l + k] = Math.min(65535, x2 - x);
				x = x2;
			}
		}
	}
	return out;
}

/**
 * RIGHE E QUADRETTI. Cerca, nella maschera "debole" (anche i segni chiari), le linee dritte orizzontali e verticali:
 * per ogni angolo da -4° a +4° conta i punti neri lungo le rette; l'angolo giusto e' quello con i picchi piu' netti. Una
 * riga del quaderno e' un picco STRETTO (le righe vicine sono quasi vuote) e LUNGO (copre almeno COPERTURA del foglio).
 * Si tolgono solo le righe che si RIPETONO a passo regolare (almeno 3, come i quaderni) oppure che attraversano quasi
 * tutto il foglio (margini): le gambe degli omini o i muri di una casa disegnati restano.
 * Dove c'e' una riga si tolgono solo i punti chiari quanto la riga: l'inchiostro piu' scuro che ci passa sopra resta.
 */
function togliRighe(b: Uint8Array, deboli: Uint8Array, r: Float32Array, l: number, a: number, t: number, fasce: Uint8Array): number {
	const COPERTURA = 0.3, COPERTURA_SOLA = 0.6;
	const lh = Math.ceil(l / 2), ah = Math.ceil(a / 2);
	const xs: number[] = [], ys: number[] = [];
	for (let y = 0; y < ah; y++)
		for (let x = 0; x < lh; x++) {
			let n = 0;
			for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) { const yy = y * 2 + dy, xx = x * 2 + dx; if (yy < a && xx < l && deboli[yy * l + xx]) n = 1; }
			if (n) { xs.push(x); ys.push(y); }
		}
	const ANG: number[] = [];
	for (let d = -4; d <= 4.001; d += 0.25) ANG.push(Math.tan((d * Math.PI) / 180));
	let tolte = 0;
	const vr = strisce(b, l, a, true), hr = strisce(b, l, a, false);
	const limite = 2 * t + 2;
	for (const vert of [false, true]) {
		const lungo = vert ? ah : lh;
		const pad = Math.ceil(lungo * 0.08) + 2;
		const nOff = (vert ? lh : ah) + 2 * pad;
		let meglio = -1, tanM = 0, contaM: Float64Array | null = null;
		for (const tg of ANG) {
			const conta = new Float64Array(nOff);
			for (let i = 0; i < xs.length; i++) {
				const o = vert ? Math.round(xs[i] + ys[i] * tg) + pad : Math.round(ys[i] - xs[i] * tg) + pad;
				if (o >= 0 && o < nOff) conta[o]++;
			}
			let punteggio = 0;
			for (let o = 0; o < nOff; o++) punteggio += conta[o] * conta[o];
			if (punteggio > meglio) { meglio = punteggio; tanM = tg; contaM = conta; }
		}
		if (!contaM) continue;
		const d = Math.max(2, Math.ceil(t / 2) + 1);
		const cand: {o: number; c: number}[] = [];
		for (let o = d; o < nOff - d; o++) {
			const c = contaM[o] / lungo;
			if (c < COPERTURA) continue;
			if (contaM[o] < contaM[o - 1] || contaM[o] < contaM[o + 1]) continue;
			const lati = Math.max(contaM[o - d], contaM[o + d]) / lungo;
			if (c < 2.2 * lati) continue;
			if (cand.length && o - cand[cand.length - 1].o <= d) { if (c > cand[cand.length - 1].c) cand[cand.length - 1] = {o, c}; continue; }
			cand.push({o, c});
		}
		// passo regolare: distanze fra righe vicine simili alla mediana
		const passi = cand.slice(1).map((q, i) => q.o - cand[i].o).sort((x, y) => x - y);
		const passo = passi.length >= 2 ? passi[Math.floor(passi.length / 2)] : 0;
		const regolare = (i: number) => {
			if (cand.length < 3 || !passo) return false;
			const ok = (j: number) => j >= 0 && j < cand.length && Math.abs(Math.abs(cand[j].o - cand[i].o) - passo) <= Math.max(2, passo * 0.25);
			// anche una riga ogni due/tre (quadretti piu' scuri, righe saltate): multipli del passo
			const multiplo = (j: number) => { if (j < 0 || j >= cand.length) return false; const q = Math.abs(cand[j].o - cand[i].o) / passo; return Math.abs(q - Math.round(q)) <= 0.2 && Math.round(q) >= 1 && Math.round(q) <= 3; };
			return ok(i - 1) || ok(i + 1) || multiplo(i - 1) || multiplo(i + 1);
		};
		for (let i = 0; i < cand.length; i++) {
			if (!regolare(i) && cand[i].c < COPERTURA_SOLA) continue;
			tolte++;
			const off = (cand[i].o - pad) * 2 + 1;
			// colore tipico della riga: mediana dell'"inchiostro" lungo la riga
			const campioni: number[] = [];
			const lun = vert ? a : l;
			for (let s = 0; s < lun; s += 3) {
				const x = vert ? Math.round(off - s * tanM) : s, y = vert ? s : Math.round(off + s * tanM);
				if (x < 0 || y < 0 || x >= l || y >= a) continue;
				const k = y * l + x;
				if (deboli[k]) campioni.push(r[k]);
			}
			campioni.sort((p, q) => p - q);
			const rigaR = campioni.length ? campioni[Math.floor(campioni.length / 2)] : 1;
			// (14 ott) solo i punti chiari quanto la riga: un tratto colorato o a matita che la attraversa e' piu' scuro e resta
			const togli = (k: number, striscia: number) => { if (b[k] && r[k] >= rigaR - 0.08 && striscia >= 0) b[k] = 0; };
			if (!vert) {
				for (let x = 0; x < l; x++) {
					const yc = Math.round(off + x * tanM);
					for (let y = yc - 2 * t - 2; y <= yc + 2 * t + 2; y++) if (y >= 0 && y < a) { const k = y * l + x; fasce[k] = 1; togli(k, vr[k]); }
				}
			} else {
				for (let y = 0; y < a; y++) {
					const xc = Math.round(off - y * tanM);
					for (let x = xc - 2 * t - 2; x <= xc + 2 * t + 2; x++) if (x >= 0 && x < l) { const k = y * l + x; fasce[k] = 1; togli(k, hr[k]); }
				}
			}
		}
	}
	return tolte;
}

/** trattini sottili (spessi al massimo 2t+2) e allungati (lunghi almeno 6t, 4 volte lo spessore) con il colore mediano
 *  chiaro (rapporto >= 0.78): sono resti di righe del quaderno */
function togliTrattiniChiari(b: Uint8Array, r: Float32Array, l: number, a: number, t: number): number {
	const visto = new Uint8Array(b.length);
	const pila = new Int32Array(b.length);
	let tolti = 0;
	for (let i = 0; i < b.length; i++) {
		if (visto[i] || !b[i]) continue;
		let p = 0;
		pila[p++] = i;
		visto[i] = 1;
		const comp: number[] = [];
		let x0 = l, y0 = a, x1 = 0, y1 = 0;
		while (p) {
			const j = pila[--p];
			comp.push(j);
			const x = j % l, y = (j - x) / l;
			if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
			for (let dy = -1; dy <= 1; dy++)
				for (let dx = -1; dx <= 1; dx++) {
					const xx = x + dx, yy = y + dy;
					if (xx < 0 || yy < 0 || xx >= l || yy >= a) continue;
					const k = yy * l + xx;
					if (!visto[k] && b[k]) { visto[k] = 1; pila[p++] = k; }
				}
		}
		const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
		const sottile = Math.min(bw, bh), lungo = Math.max(bw, bh);
		if (sottile > 2 * t + 2 || lungo < 6 * t || lungo < 4 * sottile) continue;
		const val = comp.map((j) => r[j]).sort((x, y) => x - y);
		if (val[Math.floor(val.length / 2)] < 0.78) continue;
		for (const j of comp) b[j] = 0;
		tolti++;
	}
	return tolti;
}

/** distanza (in punti) di ogni punto nero dal bianco piu' vicino: smusso 3-4 in due passate */
function distanze(b: Uint8Array, l: number, a: number): Float32Array {
	const D = new Float32Array(b.length);
	const INF = 1e9;
	for (let i = 0; i < b.length; i++) D[i] = b[i] ? INF : 0;
	for (let y = 0; y < a; y++)
		for (let x = 0; x < l; x++) {
			const k = y * l + x;
			if (!D[k]) continue;
			let v = D[k];
			if (x > 0) v = Math.min(v, D[k - 1] + 3);
			if (y > 0) { v = Math.min(v, D[k - l] + 3); if (x > 0) v = Math.min(v, D[k - l - 1] + 4); if (x < l - 1) v = Math.min(v, D[k - l + 1] + 4); }
			if (x === 0 || y === 0 || x === l - 1 || y === a - 1) v = Math.min(v, 3);
			D[k] = v;
		}
	for (let y = a - 1; y >= 0; y--)
		for (let x = l - 1; x >= 0; x--) {
			const k = y * l + x;
			if (!D[k]) continue;
			let v = D[k];
			if (x < l - 1) v = Math.min(v, D[k + 1] + 3);
			if (y < a - 1) { v = Math.min(v, D[k + l] + 3); if (x < l - 1) v = Math.min(v, D[k + l + 1] + 4); if (x > 0) v = Math.min(v, D[k + l - 1] + 4); }
			D[k] = v;
		}
	for (let i = 0; i < D.length; i++) D[i] /= 3;
	return D;
}

/** toglie le grandi zone nere PIENE che toccano il bordo della foto (bordo del tavolo, ombra, copertina del quaderno) */
function togliBordiPieni(b: Uint8Array, l: number, a: number, t: number, dist: Float32Array): number {
	const visto = new Uint8Array(b.length);
	const pila = new Int32Array(b.length);
	let tolte = 0;
	const minArea = 0.003 * l * a;
	for (let i = 0; i < b.length; i++) {
		if (visto[i] || !b[i]) continue;
		let p = 0;
		pila[p++] = i;
		visto[i] = 1;
		const comp: number[] = [];
		let tocca = false, x0 = l, y0 = a, x1 = 0, y1 = 0;
		while (p) {
			const j = pila[--p];
			comp.push(j);
			const x = j % l, y = (j - x) / l;
			if (x === 0 || y === 0 || x === l - 1 || y === a - 1) tocca = true;
			if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
			for (let dy = -1; dy <= 1; dy++)
				for (let dx = -1; dx <= 1; dx++) {
					const xx = x + dx, yy = y + dy;
					if (xx < 0 || yy < 0 || xx >= l || yy >= a) continue;
					const k = yy * l + xx;
					if (!visto[k] && b[k]) { visto[k] = 1; pila[p++] = k; }
				}
		}
		const pieno = comp.length / ((x1 - x0 + 1) * (y1 - y0 + 1));
		const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
		// linea dritta lunga e sottile rimasta da sola (margine del quaderno, bordo del foglio)
		const lineaSola = (bh >= 0.6 * a && bw <= 4 * t + 2) || (bw >= 0.6 * l && bh <= 4 * t + 2);
		// zona spessa sul bordo (tavolo, ombra): distanza media dal bianco grande (i tratti disegnati sono sottili)
		let somma = 0;
		for (const j of comp) somma += dist[j];
		const spessa = somma / comp.length > 1.2 * t;
		if (lineaSola || (tocca && comp.length > minArea && (pieno > 0.5 || spessa))) {
			for (const j of comp) b[j] = 0;
			tolte++;
		}
	}
	return tolte;
}

/** ingrossa il nero di r punti (dilatazione quasi rotonda: passi a croce e a quadrato alternati) */
function ingrossa(b: Uint8Array, l: number, a: number, r: number): Uint8Array {
	let cur = b;
	for (let passo = 0; passo < r; passo++) {
		const nuovo = new Uint8Array(cur.length);
		const quadrato = passo % 2 === 1;
		for (let y = 0; y < a; y++)
			for (let x = 0; x < l; x++) {
				const k = y * l + x;
				if (cur[k]) { nuovo[k] = 1; continue; }
				let v = 0;
				if (x > 0 && cur[k - 1]) v = 1;
				else if (x < l - 1 && cur[k + 1]) v = 1;
				else if (y > 0 && cur[k - l]) v = 1;
				else if (y < a - 1 && cur[k + l]) v = 1;
				else if (quadrato && ((x > 0 && y > 0 && cur[k - l - 1]) || (x < l - 1 && y > 0 && cur[k - l + 1]) || (x > 0 && y < a - 1 && cur[k + l - 1]) || (x < l - 1 && y < a - 1 && cur[k + l + 1]))) v = 1;
				nuovo[k] = v;
			}
		cur = nuovo;
	}
	return cur;
}

/**
 * Prepara la foto come stencil. Restituisce il nuovo file (PNG bianco e nero) e qualche dato per le prove.
 * Se qualcosa va storto (formato non leggibile dal browser) restituisce null: si usera' la foto com'e'.
 */
export async function preparaStencil(file: File, tipo: "image/png" | "image/jpeg" = "image/png"): Promise<{file: File; info: InfoStencil; tela: HTMLCanvasElement} | null> {
	const t0 = performance.now();
	let bmp: ImageBitmap;
	try {
		bmp = await createImageBitmap(file, {imageOrientation: "from-image"} as ImageBitmapOptions);
	} catch {
		return null;
	}
	const k = Math.min(1, LATO_MAX / Math.max(bmp.width, bmp.height));
	const l = Math.max(1, Math.round(bmp.width * k)), a = Math.max(1, Math.round(bmp.height * k));
	const c = tela(l, a);
	const ctx = c.getContext("2d", {willReadFrequently: true})!;
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, l, a);
	ctx.drawImage(bmp, 0, 0, l, a);
	bmp.close?.();
	const px = ctx.getImageData(0, 0, l, a).data;
	// 2-3) carta per canale e "inchiostro" = rapporto piu' basso fra i tre canali
	const r = new Float32Array(l * a).fill(1.2);
	const rL = new Float32Array(l * a); // luminosita' rispetto alla carta (per riconoscere i riempimenti chiari)
	const PESO = [0.299, 0.587, 0.114];
	for (let ch = 0; ch < 3; ch++) {
		let v: Float32Array = new Float32Array(l * a);
		for (let i = 0; i < v.length; i++) v[i] = px[i * 4 + ch] / 255;
		v = sfoca(v, l, a, 1);
		const fondo = fondoCanale(v, l, a);
		for (let i = 0; i < v.length; i++) { const q = v[i] / fondo[i]; r[i] = Math.min(r[i], q); rL[i] += PESO[ch] * Math.min(1.2, q); }
	}
	for (let i = 0; i < r.length; i++) if (r[i] > 1.2) r[i] = 1.2;
	// 4) soglia automatica
	const soglia = Math.min(SOGLIA_MAX, Math.max(SOGLIA_MIN, otsu(r)));
	let b: Uint8Array = new Uint8Array(l * a); // 1 = nero (da incidere)
	const deboli = new Uint8Array(l * a);
	for (let i = 0; i < b.length; i++) {
		if (r[i] < soglia) b[i] = 1;
		if (r[i] < SOGLIA_DEBOLE) deboli[i] = 1;
	}
	// 4b) matita chiara (Davide, 14 ott: disegni a matita): entrano anche i segni un po' piu' chiari della soglia se sono
	// piu' scuri della carta SUBITO INTORNO (linee sottili); le pieghe e le ombre morbide della carta no
	const intorno = sfoca(r, l, a, 3);
	const sogliaRidge = Math.max(0.05, 0.25 * (1 - soglia));
	for (let i = 0; i < b.length; i++) if (!deboli[i] && r[i] < 0.97 && intorno[i] - r[i] > 0.025) deboli[i] = 1; // anche le righe chiarissime, per trovarle
	// 5) righe e quadretti del foglio (prima di aggiungere la matita chiara, e la matita non si aggiunge sulle righe)
	const tRiga = Math.max(2, Math.round(Math.max(l, a) / 300));
	const fasce = new Uint8Array(l * a);
	const righe = togliRighe(b, deboli, r, l, a, tRiga, fasce);
	for (let i = 0; i < b.length; i++) if (!b[i] && !fasce[i] && r[i] < 0.92 && intorno[i] - r[i] > sogliaRidge) b[i] = 1;
	// 5b) COLORI PIENI -> CONTORNO (Davide, 14 ott: «ripassare al meglio i disegni colorati»): dentro le zone colorate
	// larghe (nuvole, sole, cielo pastellati) resta solo il bordo; i segni scuri dentro (la faccia del sole) restano; le
	// sagome scure (il cane) restano piene
	const banda = Math.max(3, Math.round(Math.max(l, a) / 250));
	const dist = distanze(b, l, a);
	const rLs = sfoca(rL, l, a, 4);
	let svuotati = 0;
	for (let i = 0; i < b.length; i++) if (b[i] && dist[i] > banda && rLs[i] > 0.55 && rL[i] > 0.6) { b[i] = 0; svuotati++; }
	// 5b2) pezzetti di riga rimasti (le righe si incurvano ai bordi del foglio): trattini sottili, lunghi e CHIARI quanto
	// una riga del quaderno; l'inchiostro e la matita sono piu' scuri e restano
	if (righe) togliTrattiniChiari(b, r, l, a, tRiga);
	// 5c) grandi zone nere piene che toccano il bordo della foto (tavolo, ombra): via
	const bordi = togliBordiPieni(b, l, a, tRiga, distanze(b, l, a));
	// 6) prima pulizia, poi ritaglio attorno al disegno
	const minArea0 = Math.max(16, Math.round(Math.pow(Math.max(l, a) / 260, 2)));
	togliMacchie(b, l, a, 1, minArea0);
	let x0 = l, y0 = a, x1 = -1, y1 = -1;
	for (let y = 0; y < a; y++) for (let x = 0; x < l; x++) if (b[y * l + x]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
	if (x1 < 0) { x0 = 0; y0 = 0; x1 = l - 1; y1 = a - 1; }
	const m = Math.round(MARGINE * Math.max(x1 - x0, y1 - y0));
	x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m); x1 = Math.min(l - 1, x1 + m); y1 = Math.min(a - 1, y1 + m);
	const lo = x1 - x0 + 1, ao = y1 - y0 + 1;
	let bc: Uint8Array = new Uint8Array(lo * ao);
	for (let y = 0; y < ao; y++) for (let x = 0; x < lo; x++) bc[y * lo + x] = b[(y + y0) * l + (x + x0)];
	b = bc;
	// 7) spessore minimo 0,1 mm sul gioiello
	const pxMin = (DETTAGLIO_MIN_MM * Math.max(lo, ao)) / LATO_INCISO_MM; // pixel che fanno 0,1 mm
	let area = 0, bordo = 0;
	for (let y = 0; y < ao; y++)
		for (let x = 0; x < lo; x++) {
			const kk = y * lo + x;
			if (!b[kk]) continue;
			area++;
			if (x === 0 || y === 0 || x === lo - 1 || y === ao - 1 || !b[kk - 1] || !b[kk + 1] || !b[kk - lo] || !b[kk + lo]) bordo++;
		}
	const spessore = bordo ? (2 * area) / bordo : 0;
	const raggio = Math.max(0, Math.min(Math.ceil(pxMin / 2), Math.round((pxMin - spessore) / 2)));
	if (raggio > 0) b = ingrossa(b, lo, ao, raggio);
	togliMacchie(b, lo, ao, 1, Math.max(minArea0, Math.round(0.6 * pxMin * pxMin)));
	togliMacchie(b, lo, ao, 0, Math.round(0.5 * pxMin * pxMin));
	let neri = 0;
	for (let i = 0; i < b.length; i++) neri += b[i];
	// 8) PNG bianco e nero
	const out = tela(lo, ao);
	const octx = out.getContext("2d")!;
	const od = octx.createImageData(lo, ao);
	for (let i = 0; i < b.length; i++) {
		const v = b[i] ? 0 : 255;
		od.data[i * 4] = od.data[i * 4 + 1] = od.data[i * 4 + 2] = v;
		od.data[i * 4 + 3] = 255;
	}
	octx.putImageData(od, 0, 0);
	const blob: Blob | null = await new Promise((ok) => out.toBlob(ok, tipo, 0.95));
	if (!blob) return null;
	const nome = (file.name || "foto").replace(/\.[^.]+$/, "") + (tipo === "image/png" ? "_stencil.png" : "_stencil.jpg");
	const nuovo = new File([blob], nome, {type: tipo, lastModified: Date.now()});
	return {
		file: nuovo,
		tela: out,
		info: {larghezza: lo, altezza: ao, soglia: Math.round(soglia * 1000) / 1000, inchiostro: Math.round((neri / (lo * ao)) * 1000) / 1000, righe, svuotati, bordi, spessore_px: Math.round(spessore * 10) / 10, ingrossato_px: raggio, ms: Math.round(performance.now() - t0)},
	};
}
